import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Check,
  X,
  RotateCcw,
  Send,
  Lock,
  FileSpreadsheet,
  Building2,
  ShieldAlert,
  AlertCircle,
  CheckCircle2,
  Loader2,
  Users,
  DollarSign,
  CreditCard,
} from "lucide-react";
import * as ownerApi from "../../api/payroll/payrollApi";
import * as portalApi from "../../api/payroll/payrollPortalApi";
import PayrollStatusBadge from "../../components/payroll/PayrollStatusBadge";
import PaymentStatusBadge from "../../components/payroll/PaymentStatusBadge";
import { useStaffAuth } from "../../context/StaffAuthContext";

// Safe UI imports with fallbacks
import * as UI from "../../components/ui";
import { useToast } from "../../context/ToastContext";

const Card = UI.Card || (({ className, children }) => <div className={`bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800/80 rounded-2xl shadow-xs ${className}`}>{children}</div>);
const CardContent = UI.CardContent || (({ className, children }) => <div className={`p-5 sm:p-6 ${className}`}>{children}</div>);
const Button = UI.Button || (({ children, className, variant, size, ...props }) => (
  <button className={`px-4 py-2 rounded-xl text-xs font-bold transition-all disabled:opacity-50 ${variant === "outline" ? "border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:bg-neutral-950 dark:hover:bg-neutral-800" : "bg-indigo-600 hover:bg-indigo-700 text-white shadow-2xs"} ${className}`} {...props}>{children}</button>
));
const Alert = UI.Alert || (({ children, variant, className }) => (
  <div className={`p-3 rounded-xl text-xs font-bold ${variant === "danger" ? "bg-rose-50 text-rose-700 border border-rose-200" : "bg-emerald-50 text-emerald-700 border border-emerald-200"} ${className}`}>{children}</div>
));

const MONTHS = [
  "",
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const PAYMENT_MODES = [
  "BANK_TRANSFER",
  "UPI",
  "CASH",
  "CHEQUE",
  "ONLINE",
];

const DEBUG = true; // flip to false when done debugging

function dbg(...args) {
  if (DEBUG) console.log("[PayrollRunDetail]", ...args);
}

function formatAxiosError(err) {
  const status = err.response?.status;
  const data = err.response?.data;
  const url = err.config?.url;
  const method = err.config?.method?.toUpperCase();
  const reqBody = err.config?.data;
  return {
    message:
      data?.message ||
      data?.error ||
      err.message ||
      "Request failed",
    status,
    url,
    method,
    responseData: data,
    requestBody: (() => {
      try {
        return typeof reqBody === "string" ? JSON.parse(reqBody) : reqBody;
      } catch {
        return reqBody;
      }
    })(),
  };
}

export default function PayrollRunDetail({ isPortal = false }) {
  const api = isPortal ? portalApi : ownerApi;
  const basePath = isPortal ? "/staff-panel/payroll-runs" : "/payroll-runs";
  const { id } = useParams();

  const staffAuth = useStaffAuth() || {};
  // Owner panel → full control | Portal → only Principal can approve/reject/lock
  const canApprove = !isPortal || !!staffAuth.isPrincipal;

  const [run, setRun] = useState(null);
  const [payslips, setPayslips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [debugInfo, setDebugInfo] = useState(null);

  const toast = useToast();

  // Reject modal
  const [showReject, setShowReject] = useState(false);
  const [rejectReason, setRejectReason] = useState("");

  // Bulk payment
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkStatus, setBulkStatus] = useState("PAID");
  const [bulkMode, setBulkMode] = useState("BANK_TRANSFER");
  const [bulkDate, setBulkDate] = useState(
    () => new Date().toISOString().split("T")[0]
  );
  const [showBulkBar, setShowBulkBar] = useState(false);

  const staffIdOf = (p) =>
    typeof p.staffId === "object" ? p.staffId?._id : p.staffId;

  const load = async () => {
    setLoading(true);
    dbg("load()", { id, isPortal });
    try {
      const [runRes, slipRes] = await Promise.all([
        api.getPayrollRunById(id),
        api.getPayslipsByRun(id),
      ]);
      const runData = runRes.data?.payrollRun || runRes.data || runRes;
      const slips = Array.isArray(slipRes) ? slipRes : slipRes.data || [];
      setRun(runData);
      setPayslips(slips);
      setSelectedIds(new Set());

      dbg("load() OK", {
        runStatus: runData?.status,
        payslipCount: slips.length,
        sampleStaffId: slips[0]
          ? {
              raw: slips[0].staffId,
              typeof: typeof slips[0].staffId,
              extracted: staffIdOf(slips[0]),
            }
          : null,
        paymentStatuses: slips.map((p) => p.paymentStatus),
      });
    } catch (err) {
      const info = formatAxiosError(err);
      console.error("[PayrollRunDetail] load() FAILED", info);
      setDebugInfo(info);
      toast.error(info.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [id, isPortal]);

  const allSelected =
    payslips.length > 0 && selectedIds.size === payslips.length;
  const someSelected = selectedIds.size > 0;

  const toggleOne = (payslip) => {
    const sid = staffIdOf(payslip);
    if (!sid) {
      dbg("toggleOne: no staffId", payslip);
      return;
    }
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(sid)) next.delete(sid);
      else next.add(sid);
      return next;
    });
  };

  const toggleAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(payslips.map(staffIdOf).filter(Boolean)));
    }
  };

  const handleApprove = async () => {
    setActionLoading(true);
    try {
      const res = await api.approvePayrollRun(id);
      toast.success(res.message || "Approved");
      load();
    } catch (err) {
      const info = formatAxiosError(err);
      console.error("[PayrollRunDetail] approve FAILED", info);
      setDebugInfo(info);
      toast.error(info.message || "Approve failed");
    } finally {
      setActionLoading(false);
    }
  };

  const handlePublish = async () => {
    setActionLoading(true);
    try {
      const res = await api.publishPayrollRun(id);
      toast.success(res.message || "Published");
      load();
    } catch (err) {
      const info = formatAxiosError(err);
      console.error("[PayrollRunDetail] publish FAILED", info);
      setDebugInfo(info);
      toast.error(info.message || "Publish failed");
    } finally {
      setActionLoading(false);
    }
  };

  const handleRevert = async () => {
    setActionLoading(true);
    try {
      const res = await api.revertPayrollRun(id);
      toast.success(res.message || "Reverted to DRAFT");
      load();
    } catch (err) {
      const info = formatAxiosError(err);
      setDebugInfo(info);
      toast.error(info.message || "Revert failed");
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    setActionLoading(true);
    try {
      const res = await api.rejectPayrollRun(id, rejectReason);
      toast.success(res.message || "Rejected");
      setShowReject(false);
      setRejectReason("");
      load();
    } catch (err) {
      const info = formatAxiosError(err);
      setDebugInfo(info);
      toast.error(info.message || "Reject failed");
    } finally {
      setActionLoading(false);
    }
  };

  const handleLock = async () => {
    setActionLoading(true);
    try {
      const res = await api.lockPayrollRun(id);
      toast.success(res.message || "Locked");
      load();
    } catch (err) {
      const info = formatAxiosError(err);
      setDebugInfo(info);
      toast.error(info.message || "Lock failed");
    } finally {
      setActionLoading(false);
    }
  };

  const handleBulkPayment = async () => {
    if (!someSelected) {
      toast.error("Select at least one staff payslip");
      return;
    }

    const payload = {
      paymentStatus: bulkStatus,
      paymentMode: bulkMode,
      paymentDate: bulkDate ? new Date(bulkDate) : new Date(),
      staffIds: Array.from(selectedIds),
    };

    dbg("handleBulkPayment → calling bulkUpdatePayslipPayment", {
      runId: id,
      isPortal,
      payload,
      selectedCount: selectedIds.size,
      staffIdSamples: payslips
        .filter((p) => selectedIds.has(staffIdOf(p)))
        .slice(0, 3)
        .map((p) => ({
          payslipId: p._id,
          staffIdRaw: p.staffId,
          staffIdExtracted: staffIdOf(p),
          paymentStatus: p.paymentStatus,
        })),
    });

    setActionLoading(true);
    setDebugInfo(null);
    try {
      const res = await api.bulkUpdatePayslipPayment(id, payload);
      dbg("handleBulkPayment SUCCESS raw response:", res);

      const modified = res.data?.modified ?? res.modified ?? 0;
      toast.success(`Payment updated for ${modified} payslip(s)`);
      setShowBulkBar(false);
      load();
    } catch (err) {
      const info = formatAxiosError(err);
      console.error("[PayrollRunDetail] BULK PAYMENT FAILED", info);
      if (info.status === 404) {
        console.error(
          "[PayrollRunDetail] HINT: Backend has no bulk route. " +
            "Frontend calls PUT .../payslips/payment/bulk/:runId"
        );
      }
      setDebugInfo(info);
      toast.error(
        `[${info.status || "?"}] ${info.message}` +
          (info.url ? ` — ${info.method} ${info.url}` : "")
      );
    } finally {
      setActionLoading(false);
    }
  };

  const markAllPaidQuick = async () => {
    const payload = {
      paymentStatus: "PAID",
      paymentMode: "BANK_TRANSFER",
      paymentDate: new Date(),
    };

    dbg("markAllPaidQuick → calling bulkUpdatePayslipPayment", {
      runId: id,
      isPortal,
      payload,
    });

    setActionLoading(true);
    setDebugInfo(null);
    try {
      const res = await api.bulkUpdatePayslipPayment(id, payload);
      dbg("markAllPaidQuick SUCCESS raw response:", res);

      const modified = res.data?.modified ?? res.modified ?? 0;
      toast.success(`Marked ${modified} payslip(s) as PAID`);
      load();
    } catch (err) {
      const info = formatAxiosError(err);
      console.error("[PayrollRunDetail] MARK ALL PAID FAILED", info);
      if (info.status === 404) {
        console.error(
          "[PayrollRunDetail] HINT: Missing backend bulk endpoint " +
            "PUT /payslips/payment/bulk/:runId"
        );
      }
      setDebugInfo(info);
      toast.error(
        `[${info.status || "?"}] ${info.message}` +
          (info.url ? ` — ${info.method} ${info.url}` : "")
      );
    } finally {
      setActionLoading(false);
    }
  };

  const pendingCount = useMemo(
    () => payslips.filter((p) => p.paymentStatus === "PENDING").length,
    [payslips]
  );

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] text-neutral-500 dark:text-neutral-400 gap-2">
        <Loader2 className="animate-spin text-indigo-600" size={28} />
        <span className="text-xs font-bold">Loading payroll run...</span>
      </div>
    );
  }

  if (!run) {
    return (
      <div className="max-w-6xl mx-auto p-4 sm:p-6 text-center text-neutral-400 dark:text-neutral-500 font-extrabold text-xs">
        Payroll run not found
      </div>
    );
  }

  const isPublished = run.status === "PUBLISHED";
  const isLocked = run.status === "LOCKED";

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto space-y-6">
      {/* Top Header Controls Bar */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 pb-4 border-b border-neutral-200 dark:border-neutral-800/80">
        <div>
          <Link
            to={basePath}
            className="inline-flex items-center text-xs font-extrabold text-neutral-500 dark:text-neutral-400 hover:text-indigo-600 transition-colors mb-1"
          >
            <ArrowLeft size={14} className="mr-1" /> Back to Runs
          </Link>
          <div className="flex items-center gap-3 mt-1">
            <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100 tracking-tight">
              Payroll — {MONTHS[run.month]} {run.year}
            </h1>
            <PayrollStatusBadge status={run.status} />
          </div>
          {run.status === "REJECTED" && run.rejectionReason && (
            <p className="text-xs font-bold text-rose-600 mt-1">
              Reason: {run.rejectionReason}
            </p>
          )}
        </div>

        {/* Dynamic Action Buttons */}
        <div className="flex flex-wrap items-center gap-2">
          {/* DRAFT — only Admin / Principal */}
          {run.status === "DRAFT" && canApprove && (
            <>
              <Button
                type="button"
                disabled={actionLoading}
                onClick={handleApprove}
                className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white"
              >
                <Check size={14} /> Approve
              </Button>
              <Button
                type="button"
                disabled={actionLoading}
                onClick={() => setShowReject(true)}
                className="flex items-center gap-1.5 bg-rose-600 hover:bg-rose-700 text-white"
              >
                <X size={14} /> Reject
              </Button>
            </>
          )}
          {run.status === "DRAFT" && !canApprove && (
            <span className="px-3 py-1.5 text-xs font-extrabold text-amber-800 bg-amber-50 border border-amber-200 rounded-xl">
              Waiting for Admin / Principal approval
            </span>
          )}

          {/* APPROVED */}
          {run.status === "APPROVED" && (
            <>
              <Button
                type="button"
                disabled={actionLoading}
                onClick={handlePublish}
                className="flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                <Send size={14} /> Publish
              </Button>
              {canApprove && (
                <>
                  <Button
                    type="button"
                    disabled={actionLoading}
                    onClick={handleRevert}
                    variant="outline"
                    className="flex items-center gap-1.5"
                  >
                    <RotateCcw size={14} /> Revert to Draft
                  </Button>
                  <Button
                    type="button"
                    disabled={actionLoading}
                    onClick={() => setShowReject(true)}
                    className="flex items-center gap-1.5 bg-rose-600 hover:bg-rose-700 text-white"
                  >
                    <X size={14} /> Reject
                  </Button>
                </>
              )}
            </>
          )}

          {/* REJECTED */}
          {run.status === "REJECTED" && canApprove && (
            <Button
              type="button"
              disabled={actionLoading}
              onClick={handleRevert}
              variant="outline"
              className="flex items-center gap-1.5"
            >
              <RotateCcw size={14} /> Revert to Draft
            </Button>
          )}

          {/* PUBLISHED */}
          {isPublished && (
            <>
              <Link to={`${basePath}/${id}/register`}>
                <Button variant="outline" className="flex items-center gap-1.5 text-indigo-700 border-indigo-200 hover:bg-indigo-50">
                  <FileSpreadsheet size={14} /> Register
                </Button>
              </Link>
              <Link to={`${basePath}/${id}/bank-transfer`}>
                <Button variant="outline" className="flex items-center gap-1.5 text-indigo-700 border-indigo-200 hover:bg-indigo-50">
                  <Building2 size={14} /> Bank File
                </Button>
              </Link>
              <Link to={`${basePath}/${id}/statutory`}>
                <Button variant="outline" className="flex items-center gap-1.5 text-indigo-700 border-indigo-200 hover:bg-indigo-50">
                  <ShieldAlert size={14} /> Statutory
                </Button>
              </Link>
              <Button
                type="button"
                disabled={actionLoading || pendingCount === 0}
                onClick={markAllPaidQuick}
                className="flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                <CheckCircle2 size={14} /> Mark All Paid
              </Button>
              <Button
                type="button"
                onClick={() => setShowBulkBar((v) => !v)}
                variant="outline"
                className="flex items-center gap-1.5 text-indigo-700 border-indigo-200 hover:bg-indigo-50"
              >
                <CreditCard size={14} /> {showBulkBar ? "Hide Bulk Bar" : "Bulk Payment"}
              </Button>
              {canApprove && (
                <Button
                  type="button"
                  disabled={actionLoading}
                  onClick={handleLock}
                  className="flex items-center gap-1.5 bg-purple-600 hover:bg-purple-700 text-white"
                >
                  <Lock size={14} /> Lock
                </Button>
              )}
            </>
          )}

          {/* LOCKED */}
          {isLocked && (
            <>
              <Link to={`${basePath}/${id}/register`}>
                <Button variant="outline" className="flex items-center gap-1.5 text-indigo-700 border-indigo-200 hover:bg-indigo-50">
                  <FileSpreadsheet size={14} /> Register
                </Button>
              </Link>
              <Link to={`${basePath}/${id}/bank-transfer`}>
                <Button variant="outline" className="flex items-center gap-1.5 text-indigo-700 border-indigo-200 hover:bg-indigo-50">
                  <Building2 size={14} /> Bank File
                </Button>
              </Link>
              <Link to={`${basePath}/${id}/statutory`}>
                <Button variant="outline" className="flex items-center gap-1.5 text-indigo-700 border-indigo-200 hover:bg-indigo-50">
                  <ShieldAlert size={14} /> Statutory
                </Button>
              </Link>
              <span className="px-3 py-1.5 text-xs font-extrabold text-purple-800 bg-purple-50 border border-purple-200 rounded-xl flex items-center gap-1">
                <Lock size={13} /> Locked — no further changes
              </span>
            </>
          )}
        </div>
      </div>

      
      

      {/* DEBUG PANEL */}
      {DEBUG && debugInfo && (
        <div className="border border-amber-300 rounded-2xl p-4 bg-amber-50 text-xs font-mono space-y-1 overflow-x-auto shadow-2xs">
          <p className="font-bold text-amber-900 text-xs">Debug — Last Failed Request</p>
          <p><span className="text-amber-700 font-bold">Status:</span> {String(debugInfo.status)}</p>
          <p><span className="text-amber-700 font-bold">URL:</span> {debugInfo.method} {debugInfo.url}</p>
          <p><span className="text-amber-700 font-bold">Message:</span> {debugInfo.message}</p>
          <pre className="mt-2 p-2 bg-white dark:bg-neutral-900 rounded-xl border text-[11px] max-h-40 overflow-auto">
            {JSON.stringify(
              {
                responseData: debugInfo.responseData,
                requestBody: debugInfo.requestBody,
              },
              null,
              2
            )}
          </pre>
          {debugInfo.status === 404 && (
            <p className="mt-2 text-rose-700 font-sans font-extrabold">
              Likely missing backend route: <code>PUT /api/payroll-runs/payslips/payment/bulk/:runId</code> (and portal equivalent).
            </p>
          )}
        </div>
      )}

      {/* Reject Modal Box */}
      {showReject && (
        <Card className="border-rose-200 bg-rose-50/50">
          <CardContent className="space-y-3">
            <p className="text-xs font-extrabold text-rose-800">
              Reject this payroll run?
            </p>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Optional reason (shown on list)"
              rows={2}
              className="w-full border border-rose-200 rounded-xl px-3 py-2 text-xs font-medium bg-white dark:bg-neutral-900 outline-none focus:border-rose-500 shadow-2xs"
            />
            <div className="flex items-center gap-2">
              <Button
                type="button"
                disabled={actionLoading}
                onClick={handleReject}
                className="bg-rose-600 hover:bg-rose-700 text-white"
              >
                Confirm Reject
              </Button>
              <Button
                type="button"
                onClick={() => {
                  setShowReject(false);
                  setRejectReason("");
                }}
                variant="outline"
              >
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Bulk Payment Bar */}
      {isPublished && showBulkBar && (
        <Card className="border-indigo-200 bg-indigo-50/40">
          <CardContent className="space-y-3">
            <p className="text-xs font-extrabold text-indigo-900">
              Bulk Payment Controls — Select rows below, configure parameters, and apply.
            </p>
            {DEBUG && (
              <p className="text-[11px] text-indigo-700 font-mono">
                selected staffIds: {JSON.stringify(Array.from(selectedIds))}
              </p>
            )}
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <label className="block text-[11px] font-extrabold text-neutral-600 dark:text-neutral-300 mb-1">Status</label>
                <select
                  value={bulkStatus}
                  onChange={(e) => setBulkStatus(e.target.value)}
                  className="border border-neutral-200 dark:border-neutral-800 rounded-xl px-3 py-2 text-xs font-bold bg-white dark:bg-neutral-900 shadow-2xs outline-none"
                >
                  <option value="PAID">PAID</option>
                  <option value="PENDING">PENDING</option>
                  <option value="FAILED">FAILED</option>
                </select>
              </div>
              <div>
                <label className="block text-[11px] font-extrabold text-neutral-600 dark:text-neutral-300 mb-1">Mode</label>
                <select
                  value={bulkMode}
                  onChange={(e) => setBulkMode(e.target.value)}
                  className="border border-neutral-200 dark:border-neutral-800 rounded-xl px-3 py-2 text-xs font-bold bg-white dark:bg-neutral-900 shadow-2xs outline-none"
                >
                  {PAYMENT_MODES.map((m) => (
                    <option key={m} value={m}>
                      {m.replace(/_/g, " ")}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-[11px] font-extrabold text-neutral-600 dark:text-neutral-300 mb-1">Date</label>
                <input
                  type="date"
                  value={bulkDate}
                  onChange={(e) => setBulkDate(e.target.value)}
                  className="border border-neutral-200 dark:border-neutral-800 rounded-xl px-3 py-2 text-xs font-bold bg-white dark:bg-neutral-900 shadow-2xs outline-none"
                />
              </div>
              <Button
                type="button"
                disabled={actionLoading || !someSelected}
                onClick={handleBulkPayment}
                className="bg-indigo-600 hover:bg-indigo-700 text-white"
              >
                Apply to {selectedIds.size || 0} Selected
              </Button>
              <Button
                type="button"
                onClick={toggleAll}
                variant="outline"
              >
                {allSelected ? "Clear Selection" : "Select All"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Overview Stat Cards Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="p-4 flex items-center gap-3">
          <div className="p-2.5 bg-indigo-50 text-indigo-600 rounded-xl shrink-0">
            <Users size={20} />
          </div>
          <div>
            <p className="text-[11px] font-extrabold text-neutral-400 dark:text-neutral-500 uppercase tracking-wider">Staff Count</p>
            <p className="text-xl font-bold text-neutral-900 dark:text-neutral-100 mt-0.5">{run.totalStaffCount}</p>
          </div>
        </Card>

        <Card className="p-4 flex items-center gap-3">
          <div className="p-2.5 bg-slate-100 dark:bg-neutral-900 text-neutral-700 dark:text-neutral-200 rounded-xl shrink-0">
            <DollarSign size={20} />
          </div>
          <div>
            <p className="text-[11px] font-extrabold text-neutral-400 dark:text-neutral-500 uppercase tracking-wider">Gross Pay</p>
            <p className="text-xl font-bold text-neutral-900 dark:text-neutral-100 mt-0.5">₹{Number(run.totalGrossPay || 0).toLocaleString()}</p>
          </div>
        </Card>

        <Card className="p-4 flex items-center gap-3">
          <div className="p-2.5 bg-rose-50 text-rose-600 rounded-xl shrink-0">
            <DollarSign size={20} />
          </div>
          <div>
            <p className="text-[11px] font-extrabold text-neutral-400 dark:text-neutral-500 uppercase tracking-wider">Deductions</p>
            <p className="text-xl font-bold text-rose-600 mt-0.5">₹{Number(run.totalDeductions || 0).toLocaleString()}</p>
          </div>
        </Card>

        <Card className="p-4 flex items-center gap-3">
          <div className="p-2.5 bg-emerald-50 text-emerald-600 rounded-xl shrink-0">
            <DollarSign size={20} />
          </div>
          <div>
            <p className="text-[11px] font-extrabold text-neutral-400 dark:text-neutral-500 uppercase tracking-wider">Net Pay</p>
            <p className="text-xl font-bold text-emerald-700 mt-0.5">₹{Number(run.totalNetPay || 0).toLocaleString()}</p>
          </div>
        </Card>
      </div>

      {/* Table Section */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-bold text-neutral-900 dark:text-neutral-100">Payslips Breakdown</h2>
          {isPublished && (
            <span className="text-xs font-bold text-amber-700 bg-amber-50 px-2.5 py-1 border border-amber-200 rounded-full">
              {pendingCount} pending payment
            </span>
          )}
        </div>

        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-xs text-left">
              <thead className="bg-slate-50/80 text-neutral-600 dark:text-neutral-300 font-extrabold border-b border-neutral-200 dark:border-neutral-800">
                <tr>
                  {isPublished && (
                    <th className="px-3 py-3.5">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={toggleAll}
                        className="w-4 h-4 rounded border-slate-300 dark:border-neutral-700 text-indigo-600 focus:ring-indigo-500"
                        title="Select all"
                      />
                    </th>
                  )}
                  <th className="px-4 py-3.5">Staff</th>
                  <th className="px-4 py-3.5">Gross</th>
                  <th className="px-4 py-3.5">LOP</th>
                  <th className="px-4 py-3.5">Half / Late / OT</th>
                  <th className="px-4 py-3.5">Net</th>
                  <th className="px-4 py-3.5">Payment</th>
                  <th className="px-4 py-3.5 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-neutral-800 font-medium text-neutral-700 dark:text-neutral-200">
                {payslips.map((p) => {
                  const sid = staffIdOf(p);
                  const checked = sid ? selectedIds.has(sid) : false;
                  return (
                    <tr
                      key={p._id}
                      className={checked ? "bg-indigo-50/40 transition-colors" : "hover:bg-neutral-50 dark:hover:bg-neutral-800/60 transition-colors"}
                    >
                      {isPublished && (
                        <td className="px-3 py-3.5">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleOne(p)}
                            className="w-4 h-4 rounded border-slate-300 dark:border-neutral-700 text-indigo-600 focus:ring-indigo-500"
                          />
                        </td>
                      )}
                      <td className="px-4 py-3.5 font-bold text-neutral-900 dark:text-neutral-100">
                        <div>{p.staffId?.name || "—"}</div>
                        <div className="text-[10px] text-neutral-400 dark:text-neutral-500 font-mono mt-0.5">
                          {p.staffId?.staffCode}
                          {DEBUG && (
                            <span className="ml-1 text-amber-600">
                              sid:{String(sid || "∅").slice(-6)}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3.5 font-extrabold text-neutral-800 dark:text-neutral-100 tabular-nums">
                        ₹{Number(p.grossPay || 0).toLocaleString()}
                        {(p.overtimePay || 0) > 0 && (
                          <div className="text-[10px] text-emerald-600 font-bold">
                            incl. OT ₹{Number(p.overtimePay).toLocaleString()}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3.5 font-bold text-amber-700">{p.lopDays || 0}d</td>
                      <td className="px-4 py-3.5 text-[11px] text-neutral-500 dark:text-neutral-400 font-semibold space-y-0.5">
                        <div>H: {p.halfDays || 0}</div>
                        <div>
                          L: {p.lateCount || 0}
                          {(p.lateLopDays || 0) > 0 && ` → ${p.lateLopDays} LOP`}
                        </div>
                        <div>OT: {p.overtimeMinutes || 0}m</div>
                      </td>
                      <td className="px-4 py-3.5 font-bold text-emerald-700 tabular-nums">
                        ₹{Number(p.netPay || 0).toLocaleString()}
                      </td>
                      <td className="px-4 py-3.5">
                        <PaymentStatusBadge status={p.paymentStatus} />
                        {p.paymentMode && (
                          <div className="text-[10px] font-bold text-neutral-400 dark:text-neutral-500 mt-1 uppercase">
                            {String(p.paymentMode).replace(/_/g, " ")}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3.5 text-right">
                        {isPublished && (
                          <Link
                            to={`${basePath}/payslips/${p._id}/payment`}
                            className="text-xs font-extrabold text-indigo-600 hover:text-indigo-800 transition-colors"
                          >
                            Payment
                          </Link>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
}